import { useCallback, useEffect, useReducer } from 'react'
import produce from 'immer'
import { ZoomClient, MediaStream } from '../pages/meeting-view/types/video-types'
import { usePermissions } from '../context/PermissionsContext'

export default function usePermissionChange(zoomClient: ZoomClient, mediaStream: MediaStream | null) {
    const { camera, microphone, checking } = usePermissions()
    const [permissionState, dispatch] = useReducer(mediaReducer, permissionShape)

    // Map browser permissions to Zoom permission states
    useEffect(() => {
        dispatch({ 
            type: 'camera', 
            payload: camera ? 'granted' : 'denied' 
        })
        
        dispatch({ 
            type: 'microphone', 
            payload: microphone ? 'granted' : 'denied' 
        })
    }, [camera, microphone])

    const onDevicePermissionChange = useCallback(
        async (payload: { name: PermissionShapeKey; state: 'denied' | 'granted' | 'prompt' }) => {
            const { name, state } = payload
            
            // Update the reducer state
            dispatch({ type: name, payload: state })
            
            // Handle Zoom-specific actions when permissions are denied
            if (state === 'denied' && mediaStream) {
                console.info('Zoom permission denied')
                await mediaStream.muteAllUserAudioLocally().catch(() => {})
                
                try {
                    const currentUserInfo = zoomClient.getCurrentUserInfo()
                    currentUserInfo.bVideoOn && (await mediaStream.stopVideo())
                    currentUserInfo.audio !== '' && (await mediaStream.stopAudio())
                } catch (error) {
                    console.info('Error stopping media when disabling permissions', error)
                }
            }
        },
        [mediaStream, zoomClient]
    )

    useEffect(() => {
        zoomClient.on('device-permission-change', onDevicePermissionChange)
        return () => {
            zoomClient.off('device-permission-change', onDevicePermissionChange)
        }
    }, [onDevicePermissionChange, zoomClient])

    return {
        permissionState,
        permissionGranted: camera || microphone,
        camera,
        microphone,
        checking,
    }
}

type PermissionShapeKey = keyof typeof permissionShape

const permissionShape = {
    microphone: {
        state: 'prompt' as 'denied' | 'granted' | 'prompt',
    },
    camera: {
        state: 'prompt' as 'denied' | 'granted' | 'prompt',
    },
}

type PermissionAction = 
    | { type: PermissionShapeKey; payload: 'denied' | 'granted' | 'prompt' }
    | { type: 'reset-permission' }

const mediaReducer = produce((draft, action: PermissionAction) => {
    switch (action.type) {
        case 'microphone': {
            draft.microphone.state = action.payload
            break
        }
        case 'camera': {
            draft.camera.state = action.payload
            break
        }
        case 'reset-permission': {
            Object.assign(draft, { ...permissionShape })
            break
        }
        default:
            break
    }
}, permissionShape)
